<template>
  <form class="profile-workspace">
    <div class="profile-grid">
      <aside class="profile-summary">
        <label class="active teal-text">Client Profile</label>
        <h5>{{ name }}</h5>
        <p>
          Primary client identity and contact details used across onboarding,
          invoicing, and administration.
        </p>

        <div class="summary-list">
          <div class="summary-item">
            <span>Scheme</span>
            <strong>{{ cat == "tpa" ? "TPA" : scheme }}</strong>
          </div>

          <div class="summary-item">
            <span>Email</span>
            <strong>{{ email || "Not provided" }}</strong>
          </div>
        </div>
      </aside>

      <section class="profile-main">
        <div v-if="error" class="profile-error">
          <i class="material-icons">error</i>
          <span>{{ error }}</span>
        </div>

        <div class="profile-card" :class="{ editing: editId == 'name' }">
          <div class="profile-copy">
            <label class="active teal-text" for="name">Client Name</label>
            <h6 v-if="editId != 'name'">{{ name || "Not provided" }}</h6>
            <div class="input-field" v-else>
              <input
                id="name"
                type="text"
                v-model="name"
                class="validate"
                required
              />
            </div>
          </div>
          <div class="profile-actions">
            <a
              v-if="editId != 'name'"
              href="#"
              class="icon-action"
              @click.prevent="handleEdit('name')"
            >
              <i class="material-icons" title="edit">edit</i>
            </a>
            <template v-else>
              <a href="#" class="text-action" @click.prevent="handleBack">
                Cancel
              </a>
              <button
                class="waves-effect waves-light btn"
                :class="{ disabled: isSubmitting }"
                @click.prevent="handleSubmit('name', name)"
              >
                Update
                <i class="material-icons right" title="update">check_circle</i>
              </button>
            </template>
          </div>
        </div>

        <div class="profile-card" :class="{ editing: editId == 'address' }">
          <div class="profile-copy">
            <label class="active teal-text" for="address">Client Address</label>
            <h6 v-if="editId != 'address'">{{ address || "Not provided" }}</h6>
            <div class="input-field" v-else>
              <input
                id="address"
                type="text"
                v-model="address"
                class="validate"
                required
              />
            </div>
          </div>
          <div class="profile-actions">
            <a
              v-if="editId != 'address'"
              href="#"
              class="icon-action"
              @click.prevent="handleEdit('address')"
            >
              <i class="material-icons" title="edit">edit</i>
            </a>
            <template v-else>
              <a href="#" class="text-action" @click.prevent="handleBack">
                Cancel
              </a>
              <button
                class="waves-effect waves-light btn"
                :class="{ disabled: isSubmitting }"
                @click.prevent="handleSubmit('address', address)"
              >
                Update
                <i class="material-icons right" title="update">check_circle</i>
              </button>
            </template>
          </div>
        </div>

        <div
          v-if="allowEmailChange"
          class="profile-card"
          :class="{ editing: editId == 'email' }"
        >
          <div class="profile-copy">
            <label class="active teal-text" for="email">Client Email</label>
            <h6 v-if="editId != 'email'">{{ email || "Not provided" }}</h6>
            <div class="input-field" v-else>
              <input
                id="email"
                type="email"
                v-model="email"
                class="validate"
                required
              />
            </div>
          </div>
          <div class="profile-actions">
            <a
              v-if="editId != 'email'"
              href="#"
              class="icon-action"
              @click.prevent="handleEdit('email')"
            >
              <i class="material-icons" title="edit">edit</i>
            </a>
            <template v-else>
              <a href="#" class="text-action" @click.prevent="handleBack">
                Cancel
              </a>
              <button
                class="waves-effect waves-light btn"
                :class="{ disabled: isSubmitting }"
                @click.prevent="handleSubmit('email', email)"
              >
                Update
                <i class="material-icons right" title="update">check_circle</i>
              </button>
            </template>
          </div>
        </div>

        <div v-else class="profile-card locked-card">
          <div class="profile-copy">
            <label class="active teal-text">Client Email</label>
            <h6>{{ email || "Not provided" }}</h6>
            <small>Email is locked after the client is active.</small>
          </div>
          <span class="locked-chip">
            <i class="material-icons">lock</i>
            Locked
          </span>
        </div>

        <div class="profile-card" :class="{ editing: editId == 'phone' }">
          <div class="profile-copy">
            <label class="active teal-text" for="phone">Client Phone</label>
            <h6 v-if="editId != 'phone'">{{ phone || "Not provided" }}</h6>
            <div class="input-field" v-else>
              <input
                id="phone"
                type="text"
                v-model="phone"
                class="validate"
                pattern="[0-9]{10,13}"
                required
              />
            </div>
          </div>
          <div class="profile-actions">
            <a
              v-if="editId != 'phone'"
              href="#"
              class="icon-action"
              @click.prevent="handleEdit('phone')"
            >
              <i class="material-icons" title="edit">edit</i>
            </a>
            <template v-else>
              <a href="#" class="text-action" @click.prevent="handleBack">
                Cancel
              </a>
              <button
                class="waves-effect waves-light btn"
                :class="{ disabled: isSubmitting }"
                @click.prevent="handleSubmit('phone', phone)"
              >
                Update
                <i class="material-icons right" title="update">check_circle</i>
              </button>
            </template>
          </div>
        </div>

        <div class="profile-card locked-card">
          <div class="profile-copy">
            <label class="active teal-text">Applicable Scheme</label>
            <h6>{{ cat == "tpa" ? "TPA" : scheme }}</h6>
            <small>Scheme assignment is managed outside profile edits.</small>
          </div>
          <span class="locked-chip">
            <i class="material-icons">lock</i>
            Locked
          </span>
        </div>
      </section>
    </div>
  </form>
</template>

<script>
import { ref, onMounted } from "vue";
import putItem from "@/composables/putItem";

export default {
  name: "ProfileClientComponent",
  props: ["cat", "client"],
  emits: ["update"],
  setup(props, context) {
    const name = ref(null);
    const address = ref(null);
    const email = ref(null);
    const allowEmailChange = ref(null);
    const phone = ref(null);
    const scheme = ref(null);
    const editId = ref(null);
    const response = ref(props.client);
    const isSubmitting = ref(false);
    const error = ref(null);

    // loader
    const handleLoad = () => {
      // reset
      editId.value = null;
      name.value = response.value.name;
      address.value = response.value.address;
      email.value = response.value.email;
      allowEmailChange.value = response.value.user_created != "y";
      phone.value = response.value.phone;
      scheme.value = "Private Scheme"; // to be updated
    };

    // update all
    const handleUpdate = () => {
      response.value.name = name.value;
      response.value.address = address.value;
      response.value.phone = phone.value;
      response.value.email = email.value;
    };

    // initialize
    onMounted(() => {
      handleLoad();
    });

    // handle Edit
    const handleEdit = (val) => {
      editId.value = val;
    };

    // handle Back
    const handleBack = () => {
      handleLoad();
    };

    // handle Submit
    const handleSubmit = async (key, val) => {
      // start
      isSubmitting.value = true;
      error.value = false;

      if (val) {
        // prepare for take off
        const newVal = {
          [key]: val,
        };

        // post item
        const { res, err } = await putItem("client", props.client.id, newVal);

        if (res.value) {
          // next
          //update all
          handleUpdate();

          // update record with parent
          context.emit("update", response.value);
        } else {
          error.value = err.value;
        }
      }

      // end here
      isSubmitting.value = false;

      // close
      handleBack();
    };

    return {
      handleEdit,
      handleBack,
      handleSubmit,
      isSubmitting,
      editId,
      error,
      name,
      address,
      email,
      allowEmailChange,
      phone,
      scheme,
    };
  },
};
</script>

<style scoped>
.profile-workspace {
  margin-top: 8px;
}

.profile-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(240px, 0.85fr) minmax(320px, 1.65fr);
}

.profile-summary,
.profile-card {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  box-shadow: none;
}

.profile-summary {
  align-self: start;
  padding: 18px;
}

.profile-summary h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 8px;
}

.profile-summary p {
  color: #607d8b;
  line-height: 1.55;
  margin: 0 0 16px;
}

.summary-list {
  border-top: 1px solid #eeeeee;
  display: grid;
  gap: 12px;
  padding-top: 14px;
}

.summary-item {
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  padding: 12px;
}

.summary-item span,
.profile-copy small {
  color: #78909c;
  display: block;
  font-size: 0.82rem;
}

.summary-item strong {
  color: #263238;
  display: block;
  font-size: 0.98rem;
  margin-top: 4px;
  overflow-wrap: anywhere;
}

.profile-main {
  display: grid;
  gap: 14px;
}

.profile-card {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  min-height: 86px;
  padding: 16px;
}

.profile-card.editing {
  background: #fafafa;
}

.profile-copy {
  min-width: 0;
}

.profile-copy h6 {
  color: #263238;
  font-size: 1.08rem;
  font-weight: 700;
  margin: 6px 0 3px;
  overflow-wrap: anywhere;
}

.profile-copy .input-field {
  margin: 8px 0 0;
  max-width: 360px;
}

.profile-actions {
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 12px;
}

.icon-action,
.locked-chip {
  align-items: center;
  border-radius: 18px;
  display: inline-flex;
  justify-content: center;
}

.icon-action {
  background: #e3f2fd;
  color: #1565c0;
  height: 38px;
  width: 38px;
}

.icon-action i {
  font-size: 20px;
}

.locked-chip {
  background: #eceff1;
  color: #607d8b;
  cursor: default;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 6px;
  padding: 7px 12px;
}

.locked-chip i {
  font-size: 17px;
}

.text-action {
  color: #607d8b;
  font-weight: 700;
}

.profile-error {
  align-items: center;
  background: #ffebee;
  border-left: 4px solid #c62828;
  border-radius: 4px;
  color: #b71c1c;
  display: flex;
  gap: 10px;
  padding: 10px 12px;
}

@media (max-width: 850px) {
  .profile-grid {
    grid-template-columns: 1fr;
  }

  .profile-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .profile-actions {
    justify-content: flex-end;
    width: 100%;
  }
}
</style>
